import { calculateCourseDateCombined } from "@shared/lib/course-date-combined"
import type { ParentCardStack } from "@shared/models/card-stack"
import type { Course, CourseCalendarDateCustom } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import moment from "moment"

export interface FollowedCourseDays {
  /** First and last day of the followed planbook's school year, if its calendar has semesters. */
  bounds: { start: string; end: string } | null
  isMeetingDay: (day: string) => boolean
}

export function followedCourseDays(
  followedCourse: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
): FollowedCourseDays {
  const semesters = rotationCalendar.attributes.semesters
  const bounds =
    semesters.length === 0
      ? null
      : {
          start: semesters.reduce(
            (start, semester) => (semester.startDate < start ? semester.startDate : start),
            semesters[0].startDate,
          ),
          end: semesters.reduce(
            (end, semester) => (semester.endDate > end ? semester.endDate : end),
            semesters[0].endDate,
          ),
        }
  const schoolDays = rotationCalendar.attributes.schoolDays
  return {
    bounds,
    isMeetingDay: (day) => {
      if (bounds !== null && (day < bounds.start || day > bounds.end)) return false
      if (schoolDays.length > 0 && !schoolDays.includes(parseDateString(day).getDay())) return false
      const courseDate = calculateCourseDateCombined(
        day,
        followedCourse,
        planbook,
        rotationCalendar,
        () => null,
      )
      return !courseDate.isOff || courseDate.hasLesson
    },
  }
}

/**
 * The day `offset` meetings of the followed class away from `dateString`, which itself needn't be
 * one. Null past the ends of its school year.
 */
export function meetingDayAtOffset(
  dateString: string,
  offset: number,
  days: FollowedCourseDays,
): string | null {
  let day: string | null = dateString
  const direction = offset < 0 ? -1 : 1
  for (let step = 0; step < Math.abs(offset) && day !== null; step++) {
    day = stepMeetingDay(day, direction, days)
  }
  return day
}

export function parseDateString(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number)
  return new Date(year, month - 1, day)
}

export function formatVisibleDateRange(firstDay: string, lastDay: string): string {
  const firstDate = moment(firstDay)
  const lastDate = moment(lastDay)

  if (firstDate.year() !== lastDate.year()) {
    return `${firstDate.format("MMMM D, YYYY")} - ${lastDate.format("MMMM D, YYYY")}`
  }
  if (firstDate.month() !== lastDate.month()) {
    return `${firstDate.format("MMMM D")} - ${lastDate.format("MMMM D, YYYY")}`
  }
  return `${firstDate.format("MMMM D")} - ${lastDate.format("D")}`
}

export function formatShortDate(dateString: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    parseDateString(dateString),
  )
}

export function copiedLessonDay(
  followedCourse: Course,
  parentCardStack: ParentCardStack,
): string | null {
  if (parentCardStack.courseId !== followedCourse.id) return null

  const copiedDate = followedCourse.attributes.calendar.dates.find(
    (date): date is CourseCalendarDateCustom =>
      date.type === "course-date-custom" && date.attributes.cardStackId === parentCardStack.id,
  )
  return copiedDate?.attributes.dateString ?? null
}

/** Without school-year bounds, how far to look for the class's next meeting before giving up. */
const MAX_DAYS_BETWEEN_MEETINGS = 366

function stepMeetingDay(
  dateString: string,
  direction: 1 | -1,
  days: FollowedCourseDays,
): string | null {
  const date = parseDateString(dateString)
  for (let step = 0; step < MAX_DAYS_BETWEEN_MEETINGS; step++) {
    date.setDate(date.getDate() + direction)
    const day = formatDateString(date)
    if (days.bounds !== null && (day < days.bounds.start || day > days.bounds.end)) return null
    if (days.isMeetingDay(day)) return day
  }
  return null
}

function formatDateString(date: Date): string {
  const pad = (part: number) => String(part).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
